import { useState, useEffect } from 'react';
import { FlaskConical, Clock, Check, AlertCircle, ChevronDown, ChevronUp, Loader2, Star, ExternalLink } from 'lucide-react';
import { useApi } from '../hooks/useApi';
import ReactMarkdown from 'react-markdown';

interface LabSummary {
  id: string;
  title: string;
  labType: string;
  difficulty: string;
  timeEstimate: number;
  questionCount: number;
  submission: {
    status: string;
    score: number | null;
    submittedAt: string;
  } | null;
}

interface LabDetail {
  id: string;
  title: string;
  labType: string;
  difficulty: string;
  scenario: string;
  sourceInfo: { title: string; url: string; platform: string; date: string } | null;
  questions: string[];
  timeEstimate: number;
  lessonTitle: string;
  courseTitle: string;
}

interface SubmissionDetail {
  id: string;
  responses: string[];
  aiFeedback: string | null;
  scores: number[] | null;
  overallScore: number | null;
  status: string;
  timeSpent: number | null;
  submittedAt: string;
  expertAnalysis: string | null;
}

interface SubmitResponse {
  success: boolean;
  submission: {
    id: string;
    aiFeedback: string;
    scores: number[];
    overallScore: number;
    expertAnalysis: string | null;
  };
  streakUpdated: boolean;
  streak: {
    current: number;
    achievedMilestone: number | null;
  };
}

// Lab type labels
const LAB_TYPE_LABELS: Record<string, string> = {
  analyze_article: 'Article Analysis',
  critique_ai: 'AI Critique',
  identify_persuasion: 'Persuasion Detection',
  fact_check: 'Fact Check',
  compare_sources: 'Source Comparison',
  decode_marketing: 'Marketing Decoder',
  evaluate_argument: 'Argument Evaluation'
};

// Difficulty colors
const DIFFICULTY_COLORS: Record<string, string> = {
  beginner: 'text-green-400 bg-green-500/10',
  intermediate: 'text-yellow-400 bg-yellow-500/10',
  advanced: 'text-red-400 bg-red-500/10'
};

interface LabExerciseProps {
  lessonId: string;
}

export default function LabExercise({ lessonId }: LabExerciseProps) {
  const { fetchApi } = useApi();
  const [labs, setLabs] = useState<LabSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLab, setSelectedLab] = useState<LabDetail | null>(null);
  const [submission, setSubmission] = useState<SubmissionDetail | null>(null);
  const [loadingLab, setLoadingLab] = useState(false);
  const [responses, setResponses] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitResult, setSubmitResult] = useState<SubmitResponse | null>(null);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [showExpertAnalysis, setShowExpertAnalysis] = useState(false);

  useEffect(() => {
    loadLabs();
  }, [lessonId]);

  async function loadLabs() {
    try {
      const data = await fetchApi<{ labs: LabSummary[] }>(`/api/labs/lesson/${lessonId}`);
      setLabs(data.labs);
    } catch (err) {
      console.error('Failed to load labs:', err);
    } finally {
      setLoading(false);
    }
  }

  async function openLab(labId: string) {
    setLoadingLab(true);
    setError(null);
    try {
      const data = await fetchApi<{ lab: LabDetail; submission: SubmissionDetail | null }>(`/api/labs/${labId}`);
      setSelectedLab(data.lab);
      setSubmission(data.submission);
      setResponses(data.submission?.responses || new Array(data.lab.questions.length).fill(''));
      if (!data.submission) {
        setStartTime(Date.now());
      }
    } catch (err) {
      console.error('Failed to load lab:', err);
      setError('Failed to load lab. Please try again.');
    } finally {
      setLoadingLab(false);
    }
  }

  async function handleSubmit() {
    if (!selectedLab) return;

    // Validate all responses
    const emptyIndex = responses.findIndex(r => !r || r.trim().length < 20);
    if (emptyIndex !== -1) {
      setError(`Response ${emptyIndex + 1} must be at least 20 characters.`);
      return;
    }

    setSubmitting(true);
    setError(null);

    const timeSpent = startTime ? Math.floor((Date.now() - startTime) / 1000) : null;

    try {
      const result = await fetchApi<SubmitResponse>(`/api/labs/${selectedLab.id}/submit`, {
        method: 'POST',
        body: JSON.stringify({ responses, timeSpent })
      });
      setSubmitResult(result);
      // Update labs list to show completed status
      setLabs(prev => prev.map(lab =>
        lab.id === selectedLab.id
          ? { ...lab, submission: { status: 'graded', score: result.submission.overallScore, submittedAt: new Date().toISOString() } }
          : lab
      ));
    } catch (err) {
      console.error('Failed to submit lab:', err);
      setError('Failed to submit lab. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  function closeLab() {
    setSelectedLab(null);
    setSubmission(null);
    setResponses([]);
    setSubmitResult(null);
    setError(null);
    setStartTime(null);
    setShowExpertAnalysis(false);
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 text-accent-blue animate-spin" />
      </div>
    );
  }

  if (labs.length === 0) {
    return null;
  }

  // Full lab view (modal-like)
  if (selectedLab) {
    const isCompleted = submission || submitResult;
    const feedback = submitResult?.submission?.aiFeedback || submission?.aiFeedback;
    const expertAnalysis = submitResult?.submission?.expertAnalysis || submission?.expertAnalysis;
    const overallScore = submitResult?.submission?.overallScore ?? submission?.overallScore;

    return (
      <div className="bg-background-secondary rounded-xl border border-gray-800 overflow-hidden">
        {/* Header */}
        <div className="p-5 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border-b border-gray-800">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <FlaskConical className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold text-white">{selectedLab.title}</h3>
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <span className={`px-2 py-0.5 rounded-full ${DIFFICULTY_COLORS[selectedLab.difficulty]}`}>
                    {selectedLab.difficulty}
                  </span>
                  <span>{LAB_TYPE_LABELS[selectedLab.labType]}</span>
                </div>
              </div>
            </div>
            <button
              onClick={closeLab}
              className="text-gray-400 hover:text-white transition-colors"
            >
              Close
            </button>
          </div>
          <div className="flex items-center gap-4 text-sm text-gray-400">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              ~{selectedLab.timeEstimate} min
            </span>
            <span>{selectedLab.questions.length} questions</span>
            {isCompleted && overallScore != null && (
              <span className={`flex items-center gap-1 ${overallScore >= 70 ? 'text-green-400' : 'text-yellow-400'}`}>
                <Star className="w-4 h-4" />
                Score: {overallScore}%
              </span>
            )}
          </div>
        </div>

        {/* Loading */}
        {loadingLab && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-accent-blue animate-spin" />
          </div>
        )}

        {/* Content */}
        {!loadingLab && (
          <div className="p-5">
            {/* Scenario */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-400 mb-2 uppercase tracking-wider">Scenario</h4>
              <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                <div className="prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown>{selectedLab.scenario}</ReactMarkdown>
                </div>
                {selectedLab.sourceInfo && (
                  <div className="mt-3 pt-3 border-t border-gray-700 flex items-center gap-2 text-xs text-gray-500">
                    <span>Source: {selectedLab.sourceInfo.title}</span>
                    {selectedLab.sourceInfo.url && (
                      <a
                        href={selectedLab.sourceInfo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-accent-blue hover:underline flex items-center gap-1"
                      >
                        View <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Questions & Responses */}
            <div className="space-y-6">
              {selectedLab.questions.map((question, index) => (
                <div key={index}>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Question {index + 1}: {question}
                  </label>
                  <textarea
                    value={responses[index] || ''}
                    onChange={(e) => {
                      const newResponses = [...responses];
                      newResponses[index] = e.target.value;
                      setResponses(newResponses);
                    }}
                    disabled={!!isCompleted}
                    placeholder="Your analysis (at least 20 characters)..."
                    className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-purple-500 focus:ring-1 focus:ring-purple-500 outline-none resize-none disabled:opacity-60"
                    rows={4}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {(responses[index] || '').length}/20 characters minimum
                  </p>
                </div>
              ))}
            </div>

            {/* Error */}
            {error && (
              <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-2 text-red-400 text-sm">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            {/* Submit button */}
            {!isCompleted && (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="w-full mt-6 py-3 px-4 bg-purple-600 hover:bg-purple-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing your responses...
                  </>
                ) : (
                  <>
                    <Check className="w-4 h-4" />
                    Submit Lab
                  </>
                )}
              </button>
            )}

            {/* AI Feedback */}
            {isCompleted && feedback && (
              <div className="mt-6 p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-lg border border-purple-500/30">
                <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                  <Star className="w-4 h-4 text-purple-400" />
                  AI Feedback
                </h4>
                <div className="prose prose-invert prose-sm max-w-none">
                  <ReactMarkdown>{feedback}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Expert Analysis */}
            {isCompleted && expertAnalysis && (
              <div className="mt-4">
                <button
                  onClick={() => setShowExpertAnalysis(!showExpertAnalysis)}
                  className="flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors"
                >
                  {showExpertAnalysis ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  {showExpertAnalysis ? 'Hide' : 'Show'} Expert Analysis
                </button>
                {showExpertAnalysis && (
                  <div className="mt-3 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                    <h4 className="font-medium text-white mb-2">Expert Analysis</h4>
                    <div className="prose prose-invert prose-sm max-w-none text-gray-300">
                      <ReactMarkdown>{expertAnalysis}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Streak notification */}
            {submitResult?.streakUpdated && (
              <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg text-orange-400 text-sm">
                Streak extended! You're now at {submitResult.streak.current} days.
                {submitResult.streak.achievedMilestone && (
                  <span className="block mt-1 font-semibold">
                    Milestone reached: {submitResult.streak.achievedMilestone} days!
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // Labs list view
  return (
    <div className="bg-background-secondary rounded-xl border border-gray-800 overflow-hidden">
      <div className="p-4 border-b border-gray-800">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
            <FlaskConical className="w-4 h-4 text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Real World Labs</h3>
            <p className="text-xs text-gray-400">Apply your knowledge to real scenarios</p>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-800">
        {labs.map((lab) => (
          <button
            key={lab.id}
            onClick={() => openLab(lab.id)}
            className="w-full p-4 flex items-center justify-between hover:bg-gray-800/30 transition-colors text-left"
          >
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-medium text-white">{lab.title}</h4>
                {lab.submission && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    lab.submission.score && lab.submission.score >= 70
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {lab.submission.score}%
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-400">
                <span className={`px-2 py-0.5 rounded-full ${DIFFICULTY_COLORS[lab.difficulty]}`}>
                  {lab.difficulty}
                </span>
                <span>{LAB_TYPE_LABELS[lab.labType]}</span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  ~{lab.timeEstimate} min
                </span>
              </div>
            </div>
            <div className="ml-4">
              {lab.submission ? (
                <Check className="w-5 h-5 text-green-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-500" />
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
